import React, { useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Mail, AlertTriangle, CheckCircle2, KeyRound, Inbox } from "lucide-react";

const STATUS_STYLES = {
  sent: "bg-green-100 text-green-700 border-green-200",
  failed: "bg-red-100 text-red-700 border-red-200",
  skipped_no_key: "bg-orange-100 text-orange-700 border-orange-200",
  skipped_no_recipient: "bg-gray-100 text-gray-600 border-gray-200",
  pending: "bg-blue-100 text-blue-700 border-blue-200",
};

const TYPE_LABELS = {
  email_sent_quote_admin: "Offerte → admin",
  email_sent_contact_admin: "Contact → admin",
  email_sent_pickup_customer: "Pickup → klant",
  email_sent_pickup_employee: "Pickup → medewerker",
  email_failed: "Mislukt",
};

function fmtTs(ts) {
  if (!ts) return "—";
  return ts.slice(0, 16).replace("T", " ");
}

export default function EmailDashboardWidget() {
  const { isAdminOrOwner } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdminOrOwner()) return;
    let mounted = true;
    adminApi.get("/admin/email-stats")
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [isAdminOrOwner]);

  if (!isAdminOrOwner()) return null;

  const today = data?.today || {};
  const byType = today.by_type || {};
  const recent = data?.recent || [];
  const resendConfigured = data?.resend_configured;

  const cards = [
    { key: "sent", label: "Verzonden vandaag", value: today.sent ?? 0, icon: CheckCircle2, color: "text-green-600" },
    { key: "failed", label: "Mislukt vandaag", value: today.failed ?? 0, icon: AlertTriangle, color: "text-red-600" },
    { key: "skipped_no_key", label: "Overgeslagen (geen key)", value: today.skipped_no_key ?? 0, icon: KeyRound, color: "text-orange-600" },
    { key: "by_quote_admin", label: "Offerte → admin", value: byType.email_sent_quote_admin ?? 0, icon: Inbox, color: "text-[#0055FF]" },
    { key: "by_contact_admin", label: "Contact → admin", value: byType.email_sent_contact_admin ?? 0, icon: Inbox, color: "text-[#0055FF]" },
    { key: "by_pickup_customer", label: "Pickup → klant", value: byType.email_sent_pickup_customer ?? 0, icon: Inbox, color: "text-[#0055FF]" },
    { key: "by_pickup_employee", label: "Pickup → medewerker", value: byType.email_sent_pickup_employee ?? 0, icon: Inbox, color: "text-[#0055FF]" },
  ];

  return (
    <div className="mt-10 bg-white border border-gray-200 p-6" data-testid="email-widget">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#0055FF]" />
          <h2 className="font-chivo font-black text-lg text-[#0A192F]">E-mailstatus vandaag</h2>
        </div>
        {!loading && (
          resendConfigured ? (
            <span
              className="text-[10px] tracking-[0.2em] uppercase font-chivo font-bold px-2 py-1 bg-green-100 text-green-700 border border-green-200"
              data-testid="email-resend-configured"
            >
              Resend actief
            </span>
          ) : (
            <span
              className="text-[10px] tracking-[0.2em] uppercase font-chivo font-bold px-2 py-1 bg-orange-100 text-orange-700 border border-orange-200"
              data-testid="email-resend-missing"
            >
              Resend key ontbreekt
            </span>
          )
        )}
      </div>

      {!loading && !resendConfigured && (
        <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 px-3 py-2 mb-4" data-testid="email-warning-no-key">
          Resend API key ontbreekt. E-mails worden gelogd als <code>skipped_no_key</code> en niet live verzonden.
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.key}
              className="border border-gray-200 p-3 flex flex-col"
              data-testid={`email-card-${c.key}`}
            >
              <Icon className={`w-4 h-4 ${c.color}`} />
              <div className="mt-2 font-chivo font-black text-2xl text-[#0A192F]">
                {loading ? "—" : c.value}
              </div>
              <div className="mt-0.5 text-[11px] text-gray-600 leading-tight">{c.label}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <h3 className="text-xs uppercase tracking-[0.15em] font-chivo font-bold text-gray-500 mb-2">
          Laatste 10 e-mails
        </h3>
        {loading ? (
          <p className="text-sm text-gray-500">Laden…</p>
        ) : recent.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="email-empty-state">
            Er zijn vandaag nog geen e-mailnotificaties geregistreerd.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="block sm:table sm:table-fixed w-full text-xs" data-testid="email-recent-table">
              <thead className="hidden sm:table-header-group">
                <tr className="text-left text-gray-500 uppercase tracking-wide">
                  <th className="py-2 pr-3 font-bold">Datum/tijd</th>
                  <th className="py-2 pr-3 font-bold">Type</th>
                  <th className="py-2 pr-3 font-bold">Ontvanger</th>
                  <th className="py-2 pr-3 font-bold">Status</th>
                  <th className="py-2 pr-3 font-bold">Gekoppeld</th>
                  <th className="py-2 font-bold">Foutmelding</th>
                </tr>
              </thead>
              <tbody className="block sm:table-row-group divide-y divide-gray-100">
                {recent.map((r) => {
                  const cls = STATUS_STYLES[r.status] || "bg-gray-100 text-gray-700 border-gray-200";
                  return (
                    <tr key={r.id} className="grid grid-cols-2 sm:table-row [&>td]:min-w-0 [&>td]:[overflow-wrap:anywhere]" data-testid={`email-row-${r.id}`}>
                      <td className="py-2 pr-3 font-mono text-[11px] text-gray-600">{fmtTs(r.created_at)}</td>
                      <td className="py-2 pr-3">{TYPE_LABELS[r.type] || r.type}</td>
                      <td className="py-2 pr-3 break-all">{r.recipient || "—"}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold border ${cls}`}
                          data-testid={`email-status-${r.id}`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-[11px] text-gray-600">
                        {r.related_kind ? `${r.related_kind} ${(r.related_id || "").slice(0, 8)}…` : "—"}
                      </td>
                      <td className="py-2 text-[11px] text-red-600 break-words max-w-[220px]">
                        {r.error || ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
